import React, { useState } from "react";
import AccountInput from "../../AccountInput";
import { t } from "../../i18n";

const PatientFormModal = ({
  showModal,
  editingPatient,
  patientForm,
  handleFormChange,
  handleSavePatient,
  onClose,
  resetForm,
  validationErrors = {},
  error,
  isSaving,
  lang,
}) => {
  const [activeSection, setActiveSection] = useState("account");

  if (!showModal) return null;

  const genderOptions = [
    { value: "", label: t("selectGender", lang) || "Select Gender" },
    { value: "male", label: t("male", lang) || "Male" },
    { value: "female", label: t("female", lang) || "Female" },
    { value: "other", label: t("other", lang) || "Other" },
  ];

  const bloodTypeOptions = [
    { value: "", label: t("selectBloodType", lang) || "Select Blood Type" },
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];

  const maritalStatusOptions = [
    { value: "", label: "Choose..." },
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const workStatusOptions = [
    { value: "", label: "Choose..." },
    { value: "employed", label: "Employed" },
    { value: "unemployed", label: "Unemployed" },
    { value: "retired", label: "Retired" },
    { value: "student", label: "Student" },
  ];

  const educationOptions = [
    { value: "", label: "Choose..." },
    { value: "high_school", label: "High School" },
    { value: "bachelors", label: "Bachelor's Degree" },
    { value: "masters", label: "Master's Degree" },
    { value: "doctorate", label: "Doctorate" },
    { value: "other", label: "Other" },
  ];

  const dietaryRoutineOptions = [
    { value: "", label: "Choose..." },
    { value: "vegetarian", label: "Vegetarian" },
    { value: "vegan", label: "Vegan" },
    { value: "omnivore", label: "Omnivore" },
    { value: "keto", label: "Keto" },
    { value: "paleo", label: "Paleo" },
    { value: "other", label: "Other" },
  ];

  const exerciseFrequencyOptions = [
    { value: "", label: "Choose..." },
    { value: "never", label: "Never" },
    { value: "rarely", label: "Rarely" },
    { value: "sometimes", label: "Sometimes" },
    { value: "often", label: "Often" },
    { value: "daily", label: "Daily" },
  ];

  const relationshipOptions = [
    { value: "", label: "Choose Relationship" },
    { value: "parent", label: "Parent" },
    { value: "sibling", label: "Sibling" },
    { value: "child", label: "Child" },
    { value: "grandparent", label: "Grandparent" },
    { value: "spouse", label: "Spouse" },
    { value: "other", label: "Other" },
  ];

  const sections = [
    { id: "account", label: "Account Information" },
    { id: "contact", label: "Contact Information" },
    { id: "profile", label: "Profile Information" },
    { id: "medical", label: "Medical Information" },
    { id: "lifestyle", label: "Lifestyle Information" },
    { id: "emergency", label: "Emergency Contact" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {editingPatient
                ? t("editPatient", lang) || "Edit Patient"
                : t("addNewPatient", lang) || "Add New Patient"}
            </h2>
            <button
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSavePatient();
            }}
          >
            {/* API Error Display */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                </div>
                <div className="mt-2 text-sm text-red-700">
                  <p className="font-medium">{error}</p>
                  {error.includes("email") && (
                    <div className="mt-2 p-2 bg-red-100 rounded border border-red-200">
                      <p className="text-xs text-red-600">
                        <strong>Tip:</strong> Check if this patient already
                        exists in your patient list, or use a different email
                        address.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Validation Error Summary */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h3 className="text-sm font-medium text-red-800">
                    Please correct the following errors:
                  </h3>
                </div>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                  {Object.values(validationErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Section Navigation */}
            <div className="mb-6 flex flex-wrap gap-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>

            {/* Account Information */}
            {activeSection === "account" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  {t("accountInformation", lang) || "Account Information"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <AccountInput
                      label="Full Name *"
                      name="fullName"
                      value={patientForm.fullName}
                      onChange={handleFormChange}
                      required
                      className={
                        validationErrors.fullName
                          ? "border-red-300 focus:border-red-500"
                          : ""
                      }
                    />
                    {validationErrors.fullName && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {validationErrors.fullName}
                      </p>
                    )}
                  </div>
                  <div>
                    <AccountInput
                      label="Email *"
                      name="email"
                      type="email"
                      value={patientForm.email}
                      onChange={handleFormChange}
                      required
                      className={
                        validationErrors.email
                          ? "border-red-300 focus:border-red-500"
                          : ""
                      }
                    />
                    {validationErrors.email && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {validationErrors.email}
                      </p>
                    )}
                  </div>
                  <AccountInput
                    label="SSN"
                    name="ssn"
                    value={patientForm.ssn}
                    onChange={handleFormChange}
                    mask="000-00-0000"
                  />
                  <AccountInput
                    label="Member ID"
                    name="memberId"
                    value={patientForm.memberId}
                    onChange={handleFormChange}
                  />
                  {!editingPatient && (
                    <AccountInput
                      label="Password"
                      name="password"
                      type="password"
                      value={patientForm.password}
                      onChange={handleFormChange}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {activeSection === "contact" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  {t("contactInformation", lang) || "Contact Information"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AccountInput
                    label="Primary Phone"
                    name="primaryPhone"
                    value={patientForm.primaryPhone}
                    onChange={handleFormChange}
                    isPhoneInput
                  />
                  <AccountInput
                    label="Mobile Phone"
                    name="mobilePhone"
                    value={patientForm.mobilePhone}
                    onChange={handleFormChange}
                    isPhoneInput
                  />
                  <AccountInput
                    label="Address Line 1"
                    name="address1"
                    value={patientForm.address1}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Address Line 2"
                    name="address2"
                    value={patientForm.address2}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="City"
                    name="city"
                    value={patientForm.city}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="State"
                    name="state"
                    value={patientForm.state}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Postal Code"
                    name="postalCode"
                    value={patientForm.postalCode}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Country"
                    name="country"
                    value={patientForm.country}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
            )}

            {/* Profile Information */}
            {activeSection === "profile" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  {t("profileInformation", lang) || "Profile Information"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AccountInput
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={patientForm.dob}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Height (inches)"
                    name="height"
                    type="number"
                    value={patientForm.height}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Weight (pounds)"
                    name="weight"
                    type="number"
                    value={patientForm.weight}
                    onChange={handleFormChange}
                  />
                  <div>
                    <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={patientForm.gender}
                      onChange={handleFormChange}
                      className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                    >
                      {genderOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                      Blood Type
                    </label>
                    <select
                      name="bloodType"
                      value={patientForm.bloodType}
                      onChange={handleFormChange}
                      className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                    >
                      {bloodTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <AccountInput
                    label="Nationality"
                    name="nationality"
                    value={patientForm.nationality}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Ethnicity"
                    name="ethnicity"
                    value={patientForm.ethnicity}
                    onChange={handleFormChange}
                  />
                  <div>
                    <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                      Organ Donor
                    </label>
                    <select
                      name="organDonor"
                      value={patientForm.organDonor}
                      onChange={handleFormChange}
                      className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                    >
                      <option value="">Choose...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                      <option value="undecided">Undecided</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Medical Information */}
            {activeSection === "medical" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Medical Information
                </h3>

                {/* Insurance Information */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Insurance Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <AccountInput
                      label="Insurance Provider"
                      name="insurance"
                      value={patientForm.insurance}
                      onChange={handleFormChange}
                    />
                    <AccountInput
                      label="Policy Number"
                      name="policy"
                      value={patientForm.policy}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* Medications */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Medications & Supplements
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Search Medications
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          placeholder="Search for Medication..."
                          className="flex-1 border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-200 rounded"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Dosage
                      </label>
                      <select className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium">
                        <option>Select Dosage</option>
                        <option>10mg</option>
                        <option>20mg</option>
                        <option>50mg</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                      Details / Notes
                    </label>
                    <textarea
                      placeholder="Add Details"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
                      rows="3"
                    />
                  </div>
                </div>

                {/* Allergies */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Known Allergies
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Allergy
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          placeholder="Enter Allergy..."
                          className="flex-1 border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-200 rounded"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Details / Notes
                      </label>
                      <textarea
                        placeholder="Add Notes / Details"
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>

                {/* Medical Conditions */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Medical Conditions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Search Conditions
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          placeholder="Search for Conditions..."
                          className="flex-1 border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-200 rounded"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Notes / Details
                      </label>
                      <textarea
                        placeholder="Add Notes / Details"
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
                        rows="3"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <AccountInput
                      label="Alias"
                      name="conditionAlias"
                      value={patientForm.conditionAlias}
                      onChange={handleFormChange}
                    />
                    <AccountInput
                      label="More Info"
                      name="conditionMoreInfo"
                      value={patientForm.conditionMoreInfo}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* Medical Procedures */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Medical Procedures
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Search Procedures
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          placeholder="Search for Procedures..."
                          className="flex-1 border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-200 rounded"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Details / Notes
                      </label>
                      <textarea
                        placeholder="Add Notes / Details"
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
                        rows="3"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <AccountInput
                      label="Full Name"
                      name="procedureFullName"
                      value={patientForm.procedureFullName}
                      onChange={handleFormChange}
                    />
                    <AccountInput
                      label="Medical Code"
                      name="medicalCode"
                      value={patientForm.medicalCode}
                      onChange={handleFormChange}
                    />
                    <AccountInput
                      label="Medical Code Dotted"
                      name="medicalCodeDotted"
                      value={patientForm.medicalCodeDotted}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* Family History */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Family History Medical Conditions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Search Conditions
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          placeholder="Search for Conditions..."
                          className="flex-1 border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-200 rounded"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Family Relation
                      </label>
                      <select className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium">
                        {relationshipOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <AccountInput
                      label="Alias"
                      name="familyConditionAlias"
                      value={patientForm.familyConditionAlias}
                      onChange={handleFormChange}
                    />
                    <AccountInput
                      label="More Info"
                      name="familyConditionMoreInfo"
                      value={patientForm.familyConditionMoreInfo}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                      Notes / Details
                    </label>
                    <textarea
                      placeholder="Add Notes / Details"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Lifestyle Information */}
            {activeSection === "lifestyle" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Lifestyle Information
                </h3>

                {/* Religion Section */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Lifestyle Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Religion
                      </label>
                      <select className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium">
                        <option>Choose...</option>
                        <option>Christianity</option>
                        <option>Islam</option>
                        <option>Judaism</option>
                        <option>Hinduism</option>
                        <option>Buddhism</option>
                        <option>Other</option>
                        {/* <option>None</option> */}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Place of Worship
                      </label>
                      <select className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium">
                        <option>Choose...</option>
                        <option>Church</option>
                        <option>Mosque</option>
                        <option>Synagogue</option>
                        <option>Temple</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <AccountInput
                      label="Place of Worship Address"
                      name="placeOfWorshipAddress"
                      value={patientForm.placeOfWorshipAddress}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* Work and Home Section */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Work and Home
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Marital Status
                      </label>
                      <select
                        name="maritalStatus"
                        value={patientForm.maritalStatus}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        {maritalStatusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Work Status
                      </label>
                      <select
                        name="workStatus"
                        value={patientForm.workStatus}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        {workStatusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Education
                      </label>
                      <select
                        name="education"
                        value={patientForm.education}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        {educationOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Occupation
                      </label>
                      <select
                        name="occupation"
                        value={patientForm.occupation}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        <option value="">Choose...</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="technology">Technology</option>
                        <option value="education">Education</option>
                        <option value="business">Business</option>
                        <option value="retail">Retail</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Exercise and Diet Section */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">
                    Exercise and Diet
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Dietary Routine
                      </label>
                      <select
                        name="dietaryRoutine"
                        value={patientForm.dietaryRoutine}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        {dietaryRoutineOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                        Exercise Frequency
                      </label>
                      <select
                        name="exerciseFrequency"
                        value={patientForm.exerciseFrequency}
                        onChange={handleFormChange}
                        className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                      >
                        {exerciseFrequencyOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            {activeSection === "emergency" && (
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AccountInput
                    label="Emergency Contact Name"
                    name="emergencyName"
                    value={patientForm.emergencyName}
                    onChange={handleFormChange}
                  />
                  <AccountInput
                    label="Emergency Contact Phone"
                    name="emergencyPhone"
                    value={patientForm.emergencyPhone}
                    onChange={handleFormChange}
                    isPhoneInput
                  />
                  <AccountInput
                    label="Relationship"
                    name="emergencyRelation"
                    value={patientForm.emergencyRelation}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 bg-white rounded-xl p-6 mt-8">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                {t("cancel", lang) || "Cancel"}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`flex-1 px-6 py-3 font-semibold rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center ${
                  isSaving
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105"
                }`}
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {editingPatient ? "Updating..." : "Adding Patient..."}
                  </>
                ) : editingPatient ? (
                  t("updatePatient", lang) || "Update Patient"
                ) : (
                  t("addPatient", lang) || "Add Patient"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PatientFormModal;
